const OS_LABELS = {
  ios: 'iOS',
  android: 'Android',
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
  other: 'Unknown',
};

const CLASS_LABELS = {
  mobile: 'Mobile',
  tablet: 'Tablet',
  desktop: 'Desktop',
};

const BROWSER_LABELS = {
  chrome: 'Chrome',
  safari: 'Safari',
  firefox: 'Firefox',
  edge: 'Edge',
  other: '',
};

function touchDeviceHints() {
  if (typeof navigator === 'undefined') {
    return { ipad: false, mobile: false };
  }
  const ua = String(navigator.userAgent || '');
  const lower = ua.toLowerCase();
  const ipad =
    /ipad/.test(lower) ||
    (navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints) > 1);
  const mobile =
    /iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua) ||
    (typeof window !== 'undefined' &&
      window.matchMedia?.('(max-width: 719px)')?.matches &&
      ipad);
  return { ipad, mobile };
}

/** Parse a user-agent string into coarse device metadata. */
export function parseUserAgent(ua = '') {
  const userAgent = String(ua || '').slice(0, 300);
  const lower = userAgent.toLowerCase();
  const touch = typeof navigator !== 'undefined' && userAgent === navigator.userAgent ? touchDeviceHints() : {
    ipad: /ipad/.test(lower) || (lower.includes('macintosh') && lower.includes('mobile')),
    mobile: /iphone|ipod|android.*mobile|windows phone|blackberry/i.test(userAgent),
  };

  let deviceClass = 'desktop';
  if (touch.ipad || /tablet|playbook|silk/.test(lower)) {
    deviceClass = 'tablet';
  } else if (touch.mobile || /mobile/.test(lower)) {
    deviceClass = 'mobile';
  }

  let os = 'other';
  if (/iphone|ipad|ipod/.test(lower) || touch.ipad) {
    os = 'ios';
  } else if (/android/.test(lower)) {
    os = 'android';
    if (deviceClass === 'desktop' && !/tablet|playbook/.test(lower)) {
      deviceClass = 'mobile';
    }
  } else if (/mac os x|macintosh/.test(lower)) {
    os = 'macos';
  } else if (/windows/.test(lower)) {
    os = 'windows';
  } else if (/linux|cros/.test(lower)) {
    os = 'linux';
  }

  let platform = 'desktop';
  if (os === 'ios') platform = 'apple';
  else if (os === 'android') platform = 'android';
  else if (deviceClass === 'mobile' || deviceClass === 'tablet') platform = 'mobile';

  let browser = 'other';
  if (/edg\//.test(lower)) browser = 'edge';
  else if (/chrome\//.test(lower) && !/edg\//.test(lower)) browser = 'chrome';
  else if (/firefox\//.test(lower)) browser = 'firefox';
  else if (/safari\//.test(lower) && !/chrome\//.test(lower)) browser = 'safari';

  return {
    deviceClass,
    os,
    platform,
    browser,
    userAgent,
  };
}

export function getClientDevice() {
  if (typeof navigator === 'undefined') {
    return parseUserAgent('');
  }
  return parseUserAgent(navigator.userAgent || '');
}

export function formatDeviceLabel(device = {}) {
  const osLabel = OS_LABELS[device.os] || OS_LABELS.other;
  const classLabel = CLASS_LABELS[device.deviceClass] || device.deviceClass || '';
  const browserLabel = BROWSER_LABELS[device.browser] || device.browser || '';
  return [osLabel, classLabel, browserLabel].filter(Boolean).join(' · ');
}

export function deviceFields(device = getClientDevice()) {
  return {
    deviceClass: device.deviceClass || 'desktop',
    deviceOs: device.os || 'other',
    devicePlatform: device.platform || 'desktop',
    deviceBrowser: device.browser || 'other',
    deviceLabel: formatDeviceLabel(device),
    lastUserAgent: device.userAgent || '',
  };
}

export function deviceLabelFromProfile(profile = {}) {
  if (profile.deviceLabel) return profile.deviceLabel;
  if (profile.lastUserAgent) return formatDeviceLabel(parseUserAgent(profile.lastUserAgent));
  return '';
}

export function deviceFieldsFromProfile(profile = {}) {
  if (profile.deviceOs || profile.deviceClass || profile.deviceLabel) {
    return {
      deviceClass: profile.deviceClass || '',
      deviceOs: profile.deviceOs || '',
      devicePlatform: profile.devicePlatform || '',
      deviceBrowser: profile.deviceBrowser || '',
      deviceLabel: deviceLabelFromProfile(profile),
      lastUserAgent: profile.lastUserAgent || '',
    };
  }
  if (profile.lastUserAgent) {
    return deviceFields(parseUserAgent(profile.lastUserAgent));
  }
  return deviceFields(parseUserAgent(''));
}

let lastDeviceSignature = '';

/** Attach device fields when UA or coarse class changes (avoids redundant writes). */
export function maybeDeviceFields(device = getClientDevice()) {
  const signature = `${device.os}|${device.deviceClass}|${device.browser}|${device.userAgent}`;
  if (signature === lastDeviceSignature) return null;
  lastDeviceSignature = signature;
  return deviceFields(device);
}

export function isLikelyBotUserAgent(ua = '') {
  return /bot|crawler|spider|preview|python-requests|curl\/|wget|httpclient|scrapy|firefox\/11\.0.*windows nt 5\.1/i.test(
    String(ua || '')
  );
}
